/**
 * 48 コマの表の 3 時間 (6 コマ) ごとの区切り線 (`docs/specs/conventions.md`
 * §48 コマの表の区切り線、 #9698)。
 *
 * 48 行が同じ罫線で続くと目的のコマを見失いやすいため、 03:00 / 06:00 / … / 21:00 の
 * 境目 (= 02:30・05:30・…・20:30 のコマの行の下) に、 通常の行線と見分けられる線を引く。
 * 最終コマ (23:30) の下は表の終わりなので引かない。
 *
 * 1 コマが複数行になる表はそのコマの最後の行の下に、 コマが飛ぶ一覧は境目をまたぐ所に
 * 1 本引く ([[isKomaDividerBetween]])。
 */
import { LightModeSemanticColors } from '@enechain/ecloud-designsystem'

import { KOMA_COUNT } from '~/constants/koma'

/** 区切り線の間隔 (コマ数)。 3 時間 = 6 コマ。 */
export const KOMA_DIVIDER_INTERVAL = 6

/**
 * 区切り線の色。 通常の行線 (Border.lowEmphasis) より一段濃い Border.highEmphasis
 * (8080 `tr.slot-3h-end` の `--border-high`)。 Chakra の style prop に渡す時は
 * [[KOMA_DIVIDER_COLOR_TOKEN]]、 inline style には [[KOMA_DIVIDER_BORDER]] を使う。
 */
export const KOMA_DIVIDER_COLOR_TOKEN = 'Border.highEmphasis'

/** inline style 用の区切り線 (`border-bottom` の値)。 */
export const KOMA_DIVIDER_BORDER = `1px solid ${LightModeSemanticColors.Border.highEmphasis.value}`

/** コマ番号 (1-origin) の行の下に区切り線を引くか。 */
export function isKomaDividerAfter(koma: number): boolean {
  return koma % KOMA_DIVIDER_INTERVAL === 0 && koma < KOMA_COUNT
}

/** コマ index (0-origin) の行の下に区切り線を引くか。 */
export function isKomaIndexDividerAfter(index: number): boolean {
  return isKomaDividerAfter(index + 1)
}

/**
 * コマが飛ぶ / 1 コマが複数行になる一覧で、 コマ番号 `koma` (1-origin) の行と次の行
 * (コマ番号 `nextKoma`。 最終行なら undefined) の間に区切り線を引くか。
 * 同じコマの行どうしの間には引かず、 3 時間の境目をまたぐ所に 1 本だけ引く。
 */
export function isKomaDividerBetween(
  koma: number,
  nextKoma: number | undefined,
): boolean {
  if (nextKoma === undefined || nextKoma <= koma) return false
  // コマ番号を読めなかった行 (NaN) では引かない
  if (!Number.isFinite(koma) || !Number.isFinite(nextKoma)) return false
  return (
    Math.floor((koma - 1) / KOMA_DIVIDER_INTERVAL) !==
    Math.floor((nextKoma - 1) / KOMA_DIVIDER_INTERVAL)
  )
}

/**
 * DS の Table (`TableRoot` の line variant。 各セルが下線を持つ) の行に渡す css。
 * 区切りの行だけセルの下線を濃くする (行に border を付けても、 同じ太さのセルの線が勝つ)。
 */
export const KOMA_DIVIDER_ROW_CSS = {
  '& > td': {
    borderBottomColor: LightModeSemanticColors.Border.highEmphasis.value,
  },
} as const

/**
 * コマ順に並んだ一覧 (入札の明細など。 コマが飛ぶ・1 コマが複数行) の各行について、
 * その行の下に区切り線を引くかを返す。
 *
 * - `groupOf` が違う行どうし (受渡日が違う等) の間には引かない
 * - 同じグループの中でコマが戻る (コマ順に並んでいない) 一覧には 1 本も引かない。
 *   状態順・申請ごとに並べた一覧で、 区切りが意味を持たないため (8080 `js/slot-divider.js`
 *   と同じ扱い)
 */
export function komaListDividerFlags<T>(
  rows: readonly T[],
  komaOf: (row: T) => number,
  groupOf: (row: T) => string = () => '',
): boolean[] {
  const flags = rows.map(() => false)
  for (let i = 0; i + 1 < rows.length; i++) {
    const row = rows[i]
    const next = rows[i + 1]
    if (row === undefined || next === undefined) continue
    if (groupOf(row) !== groupOf(next)) continue
    const koma = komaOf(row)
    const nextKoma = komaOf(next)
    if (nextKoma < koma) return rows.map(() => false)
    flags[i] = isKomaDividerBetween(koma, nextKoma)
  }
  return flags
}
